import { ROUTE_MAP } from "../constants/routes";
import { getRouteLang } from "./language";

/**
 * Zwraca zlokalizowany path pattern (z :param) dla danego klucza route.
 * Przeznaczony do <Route path={routePath('dashboard')} />.
 */
export function routePath(routeKey) {
    const lang = getRouteLang();
    const route = ROUTE_MAP[routeKey];
    if (!route) {
        console.warn(`[routePath] Unknown route key: "${routeKey}"`);
        return routeKey;
    }
    return route[lang] || route.en;
}

/**
 * Sprawdza czy pathname zaczyna się od ścieżki danego route key.
 * Sprawdza obie wersje językowe (dla bezpieczeństwa przy przekierowaniach).
 *
 * pathStartsWith("/admin/komunikaty", "admin") → true
 * pathStartsWith("/pulpit", "admin") → false
 */
export function pathStartsWith(pathname, routeKey) {
    const route = ROUTE_MAP[routeKey];
    if (!route) return pathname.startsWith(routeKey);
    return pathname.startsWith(route.pl) || pathname.startsWith(route.en);
}

/**
 * Tłumaczy bieżący pathname na docelowy język.
 * Obsługuje zarówno statyczne jak i dynamiczne ścieżki (:id, :slug).
 *
 * translateCurrentPath("/pulpit", "en") → "/dashboard"
 * translateCurrentPath("/aktualnosci/moj-artykul", "en") → "/news/moj-artykul"
 * translateCurrentPath("/zgloszenia/123", "en") → "/tickets/123"
 */
export function translateCurrentPath(currentPathname, targetLang) {
    const sourceLang = targetLang === "pl" ? "en" : "pl";

    // [2026-10-01] NAJPIERW dokładne dopasowanie statyczne, dopiero potem wzorce z parametrami. Dawniej kolejność w ROUTE_MAP
    // decydowała: „/obsluga-it/procedury” łapał wcześniejszy wzorzec „/obsluga-it/:moduleCode” i wynik był „/it-management/procedury”
    // (zapadka routeHelpers.test: każda trasa odwracalna pl->en->pl). React Router też stawia trasę statyczną przed parametrem.
    for (const [, paths] of Object.entries(ROUTE_MAP)) {
        const sourcePath = paths[sourceLang];
        const targetPath = paths[targetLang];
        if (sourcePath && targetPath && !sourcePath.includes(":") && currentPathname === sourcePath) return targetPath;
    }

    for (const [, paths] of Object.entries(ROUTE_MAP)) {
        const sourcePath = paths[sourceLang];
        const targetPath = paths[targetLang];
        if (!sourcePath || !targetPath || sourcePath === targetPath) continue;

        // Statyczne ścieżki obsłużyła pętla wyżej.
        if (!sourcePath.includes(":")) continue;

        // Dynamiczne ścieżki — regex match
        const regex = new RegExp(
            "^" + sourcePath.replace(/:[^/]+/g, "([^/]+)") + "$"
        );
        const match = currentPathname.match(regex);
        if (match) {
            const paramPlaceholders = [...sourcePath.matchAll(/:([^/]+)/g)].map(m => m[1]);
            let result = targetPath;
            paramPlaceholders.forEach((param, i) => {
                result = result.replace(`:${param}`, match[i + 1]);
            });
            return result;
        }
    }

    return currentPathname; // nie znaleziono — zwróć bez zmian
}

/**
 * Tłumaczy dowolną ścieżkę (np. z backendu) na aktualny język.
 * Obsługuje query stringi.
 *
 * translatePath("/my-tickets?ticketId=123") → "/moje-zgloszenia?ticketId=123" (pl)
 */
export function translatePath(path) {
    if (!path) return path;
    const targetLang = getRouteLang();
    const [pathname, search] = path.split("?");
    const translated = translateCurrentPath(pathname, targetLang);
    return search ? `${translated}?${search}` : translated;
}
